import React, { useMemo, useCallback } from 'react';
import { UnifiedTable } from '../../../../components/UnifiedTable';

/**
 * OzonAttributesPanel — migrated to UnifiedTable.
 * Fix: image URL columns now render as thumbnails (type='image') instead of plain text.
 * Columns are built dynamically from API headers.
 */

// Headers that should be rendered as images
const IMAGE_HEADERS = ['изображение', 'фото', 'image', 'photo', 'картинка', 'миниатюра', 'preview'];

// Headers that contain product name (for OZON link)
const NAME_HEADERS = ['название', 'name', 'наименование', 'товар'];

// Headers that contain SKU
const SKU_HEADERS = ['sku', 'скю'];

function isImageHeader(header) {
    const lower = (header || '').toLowerCase();
    return IMAGE_HEADERS.some((h) => lower.includes(h));
}

function isNameHeader(header) {
    const lower = (header || '').toLowerCase();
    return NAME_HEADERS.some((h) => lower.includes(h));
}

function isSkuHeader(header) {
    const lower = (header || '').toLowerCase();
    return SKU_HEADERS.some((h) => lower === h);
}

function OzonAttributesPanel({
    attributesPageSafe,
    attributesTotalPages,
    attributesPageSize,
    setAttributesPage,
    setAttributesPageSize,
    attributesSearch,
    setAttributesSearch,
    copyTable,
    downloadCsv,
    ozonAttributes,
    attributesHeadersFull,
    attributesRowsView,
    attributesSliceStart,
    attributesSliceEnd,
    attributesSort,
    setAttributesSort,
    ATTRIBUTES_MAX_COLUMNS,
    getStickyClass,
    openLightbox,
}) {
    // Find SKU column index for OZON link
    const skuColIndex = useMemo(() => {
        return attributesHeadersFull.findIndex((h) => isSkuHeader(h));
    }, [attributesHeadersFull]);

    // Build columns dynamically from headers
    const columns = useMemo(() => {
        return attributesHeadersFull.map((header, index) => {
            const isRowNum = header === '№';
            const isArticle = header === 'Артикул';
            const isImage = isImageHeader(header);
            const isName = isNameHeader(header);

            const col = {
                key: `col_${index}`,
                header,
                type: isImage ? 'image' : isRowNum ? 'number' : isName ? 'custom' : 'text',
                width: isRowNum ? 56 : isImage ? 80 : isArticle ? 180 : isName ? 280 : 150,
                sticky: isRowNum || isArticle,
                sortable: !isRowNum,
                imageSize: 40,
            };

            // Add OZON link render for name column
            if (isName && skuColIndex >= 0) {
                col.render = (value, row) => {
                    const sku = row[`col_${skuColIndex}`];
                    if (!value) return <span style={{ opacity: 0.5 }}>-</span>;
                    if (sku) {
                        return (
                            <a
                                href={`https://ozon.ru/product/${sku}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#519ad6', textDecoration: 'none' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {value}
                            </a>
                        );
                    }
                    return value;
                };
            }

            return col;
        });
    }, [attributesHeadersFull, skuColIndex]);

    // Convert index-based sort to key-based
    const sortKey = columns[attributesSort.index]
        ? columns[attributesSort.index].key
        : null;
    const sortDir = attributesSort.dir;

    // Convert key-based sort back to index-based for App.js
    const handleSortChange = useCallback(
        (key, dir) => {
            const colIndex = columns.findIndex((c) => c.key === key);
            if (colIndex >= 0) {
                setAttributesSort({ index: colIndex, dir });
            }
        },
        [columns, setAttributesSort]
    );

    // Convert array rows to objects for UnifiedTable
    const tableData = useMemo(() => {
        return attributesRowsView.map((row) => {
            const obj = {};
            columns.forEach((col, idx) => {
                obj[col.key] = row[idx] ?? '';
            });
            return obj;
        });
    }, [attributesRowsView, columns]);

    // Image click → open lightbox
    const handleImageClick = useCallback(
        (url) => {
            if (openLightbox) openLightbox([url], 0);
        },
        [openLightbox]
    );

    // Total rows
    const totalRows = attributesTotalPages * attributesPageSize;

    return (
        <UnifiedTable
            tableId="ozon-attributes"
            columns={columns}
            data={tableData}
            totalRows={totalRows}
            page={attributesPageSafe}
            pageSize={attributesPageSize}
            onPageChange={setAttributesPage}
            onPageSizeChange={setAttributesPageSize}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            serverSort={true}
            search={attributesSearch}
            onSearchChange={setAttributesSearch}
            onImageClick={handleImageClick}
            exportFilename="ozon-attributes"
            onExportCsv={true}
            onCopyTable={true}
            loading={false}
            getRowKey={(row, index) => `attr-${row.col_0 || index}`}
        />
    );
}

export default OzonAttributesPanel;